import {
	DEFAULT_DUE_TIME,
	type ExternalFormData,
	type Form360Data,
	type StandardFormData,
} from "../../../../../../components/FormSelectionCard";

import type { ConfiguredForm, FormType } from "./types";

import { generateLocalId } from "./services/transformAssignments";

type AdHocSeed = { formId: string; formName: string };

const cycleStartAtDefaultTime = (cycleStartDate: Date, defaultDueTime: number) => {
	const d = new Date(cycleStartDate);
	d.setHours(defaultDueTime, 0, 0, 0);
	if (d < cycleStartDate) {
		d.setDate(d.getDate() + 1);
	}
	return d;
};

const buildStandard = (seed: AdHocSeed, cycleStartDate: Date, defaultDueTime: number): StandardFormData => ({
	formType: "standard",
	formName: seed.formName,
	formAuthors: [],
	formSigners: [],
	authorsDueDate: cycleStartAtDefaultTime(cycleStartDate, defaultDueTime),
	signersDueDate: cycleStartAtDefaultTime(cycleStartDate, defaultDueTime),
	isRelativeDates: false,
	makeVisibleWhenCompleted: false,
	redactAuthors: false,
});

const build360 = (seed: AdHocSeed, cycleStartDate: Date, defaultDueTime: number): Form360Data => ({
	formType: "360",
	formName: seed.formName,
	whoCanAddForm: "Any Employee",
	approval: "No Approval Needed",
	formSigners: [],
	authorsDueDate: cycleStartAtDefaultTime(cycleStartDate, defaultDueTime),
	signersDueDate: cycleStartAtDefaultTime(cycleStartDate, defaultDueTime),
	isRelativeDates: false,
	dueDatesToggle: true, // ad-hoc 360 forms use static dates per ticket
	makeVisibleWhenFinished: false,
	allow360RequestMessage: false,
	redactAuthors: false,
	makeFormInactive: false,
});

const buildExternal = (seed: AdHocSeed): ExternalFormData => ({
	formType: "external",
	formName: seed.formName,
	makeVisibleWhenFinished: false,
	redactAuthors: false,
});

export const createAdHocForm = <T extends FormType>(
	formType: T,
	seed: AdHocSeed,
	cycleStartDate: Date,
	defaultDueTime: number = DEFAULT_DUE_TIME
): ConfiguredForm<T> => {
	const base = {
		localId: generateLocalId(),
		formId: seed.formId,
		fromTemplate: false,
	} as const;

	if (formType === "standard") {
		return { ...base, data: buildStandard(seed, cycleStartDate, defaultDueTime) } as ConfiguredForm<T>;
	}
	if (formType === "360") {
		return { ...base, data: build360(seed, cycleStartDate, defaultDueTime) } as ConfiguredForm<T>;
	}
	return { ...base, data: buildExternal(seed) } as ConfiguredForm<T>;
};
